/**
 * Anonymous Business Request Service
 *
 * Creates anonymous temporary business requests via the dedicated
 * /v4/workflows/business-requests/anonymous endpoint. The BR is session-owned
 * (not user-owned) until claimed after login.
 *
 * Trust boundary: anonymous session ID (UUID stored in sessionStorage)
 * is forwarded as x-eai-anon-asset-session by apiService on all anon BR requests.
 *
 * Reference: useAnonBusinessRequest.ts (enterpriseai-packages)
 */

import { apiService } from './api.service.js';
import { getEndpoint } from '../config/endpoints.js';
import { chatStore } from '../stores/chat.store.js';
import { userStore } from '../stores/user.store.js';
import { logger } from '../utils/logger.js';
import { generateUUID } from '../utils/storage.js';

const MAX_STRUCTURED_MIGRATION_MESSAGES = 50;
const START_NOW_INTRO_PATTERN =
    /If you click <a\b[^>]*class="start-da-link"[^>]*>\s*<i><b>Start now<\/b><\/i>\s*<\/a>/i;
const PORTAL_LOGIN_INTRO_TEXT = 'If you click <i><b>Login</b></i>';

function parseTimestamp(value) {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function compareMigrationMessages(left, right) {
    const leftTimestamp = parseTimestamp(left.timestamp);
    const rightTimestamp = parseTimestamp(right.timestamp);

    if (leftTimestamp !== null && rightTimestamp !== null && leftTimestamp !== rightTimestamp) {
        return leftTimestamp - rightTimestamp;
    }

    if (leftTimestamp !== null && rightTimestamp === null) {
        return -1;
    }

    if (leftTimestamp === null && rightTimestamp !== null) {
        return 1;
    }

    return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
}

function normalizeStructuredMigrationMessageType(rawType) {
    return rawType === 'address_card' ? 'address_card' : 'text';
}

function normalizeHandoffMessage(rawMessage, index = 0) {
    if (!rawMessage || typeof rawMessage !== 'object') {
        return null;
    }

    const type = typeof rawMessage.type === 'string' ? rawMessage.type : '';
    const name = typeof rawMessage.name === 'string' ? rawMessage.name : '';
    const sender = typeof rawMessage.sender === 'string' ? rawMessage.sender : '';
    const rawContent =
        typeof rawMessage.message === 'string'
            ? rawMessage.message
            : typeof rawMessage.text === 'string'
              ? rawMessage.text
              : '';
    const isAddressCard = type === 'address_card' || name === 'AddressCard';
    const message = isAddressCard
        ? rawContent.trim()
        : rawContent.replace(START_NOW_INTRO_PATTERN, PORTAL_LOGIN_INTRO_TEXT).trim();

    if (!message && !isAddressCard) {
        return null;
    }

    if (type === 'streaming' || type === 'get_started_card' || name === 'GetStartedCard') {
        return null;
    }

    let normalizedName = null;
    if (isAddressCard) {
        normalizedName = 'User';
    } else if (name === 'User' || sender === 'user' || type === 'user') {
        normalizedName = 'User';
    } else if (
        name === 'Bot' ||
        sender === 'bot' ||
        type === 'bot' ||
        type === 'address_follow_up' ||
        type.startsWith('genesys_')
    ) {
        normalizedName = 'Bot';
    }

    if (!normalizedName) {
        return null;
    }

    const parsedTimestamp = Date.parse(rawMessage.timestamp);

    return {
        id: typeof rawMessage.id === 'string' ? rawMessage.id : generateUUID(),
        name: normalizedName,
        message,
        messageType: type || rawMessage.messageType || undefined,
        timestamp: Number.isNaN(parsedTimestamp) ? undefined : new Date(parsedTimestamp + index).toISOString()
    };
}

function buildStructuredHistoryMigrationMessages(options = {}) {
    const excludedIds = new Set(options.excludeMessageIds || []);

    return (chatStore.getState()?.messages || [])
        .map((message, index) => ({ message, index }))
        .filter(({ message }) => !message?.persistedMessageId)
        .filter(({ message }) => !excludedIds.has(message?.id))
        .map(({ message, index }) => {
            const normalizedMessage = normalizeHandoffMessage(message, index);
            return normalizedMessage ? { ...normalizedMessage, sortOrder: index } : null;
        })
        .filter(Boolean)
        .sort(compareMigrationMessages)
        .slice(-MAX_STRUCTURED_MIGRATION_MESSAGES)
        .map(({ sortOrder, ...message }) => message);
}

function buildStructuredHistoryMigrationPayload(options = {}) {
    const messages = buildStructuredHistoryMigrationMessages(options);
    if (!messages.length) {
        return null;
    }

    const userState = userStore.getState();
    const stage = userState?.address && userState?.propId ? 'planning' : 'property';

    return {
        localMessageIds: messages.map((message) => message.id),
        conversation_id: chatStore.conversationId || undefined,
        stage,
        messages: messages.map((message) => ({
            role:
                normalizeStructuredMigrationMessageType(message.messageType) === 'address_card'
                    ? 'assistant'
                    : message.name === 'User'
                      ? 'user'
                      : 'assistant',
            content: message.message,
            message_type: normalizeStructuredMigrationMessageType(message.messageType),
            created_at: message.timestamp
        }))
    };
}

/**
 * Get or create the anonymous session ID.
 * Persisted in sessionStorage for the duration of the browser session.
 */
function getOrCreateAnonSessionId() {
    let sessionId = sessionStorage.getItem('eaiAnonAssetSession');
    if (!sessionId) {
        sessionId = generateUUID();
        sessionStorage.setItem('eaiAnonAssetSession', sessionId);
    }
    return sessionId;
}

function hasAnonSessionId() {
    return !!sessionStorage.getItem('eaiAnonAssetSession');
}

function clearAnonSessionId() {
    sessionStorage.removeItem('eaiAnonAssetSession');
}

/**
 * AnonBusinessRequestService class
 */
class AnonBusinessRequestService {
    /**
     * Check whether the widget still has an active anonymous session pointer.
     *
     * @returns {boolean}
     */
    hasActiveSession() {
        return hasAnonSessionId();
    }

    /**
     * Clear the widget's anonymous session pointer.
     * Used for intentional local resets so the next BR starts cleanly.
     */
    clearSession() {
        clearAnonSessionId();
    }

    /**
     * Create an anonymous temporary business request.
     * Uses the dedicated /v4/workflows/business-requests/anonymous endpoint.
     * The BR is session-owned via x-eai-anon-asset-session header.
     *
     * @param {Object} params - Creation parameters
     * @param {string} params.tenantId - Tenant/organization ID
     * @param {string} params.workflowId - Workflow identifier
     * @param {string} [params.email] - User email (optional)
     * @param {string} [params.name] - Business request name
     * @param {Object} [params.customData] - Custom data to include
     * @returns {Promise<Object|null>} - Created BR or null on failure
     */
    async createAnonBusinessRequest(params, options = {}) {
        const { tenantId, workflowId, email = '', name = 'My Project', customData = {} } = params;

        const endpoint = getEndpoint('anonBusinessRequest');
        if (!endpoint) {
            logger.warn('[AnonBRService] anonBusinessRequest endpoint not configured');
            return null;
        }

        getOrCreateAnonSessionId();

        try {
            logger.debug('[AnonBRService] Creating anon temp BR:', { tenantId, workflowId, name });

            // Tenant is server-authoritative: apiService sends it as the `tenant`
            // header (see api.service.js resolveTenantId), never in the body.
            // PublicAPI's AnonymousBusinessRequestCreateRequest model is extra="forbid",
            // so a top-level tenant_id 422s (VALIDATION_ERROR) on both v3 and v4.
            // See Issues2025#3077.
            const body = {
                workflow_id: workflowId,
                business_request_name: name,
                email: email || undefined,
                custom_data: customData
            };
            // Force ResourceAPI storage on create (Issues2025#3077 "no Payload" directive).
            // Payload is being deprecated, so the widget explicitly targets ResourceAPI on the
            // create instead of relying on tenant auto-routing (Issues2025#3083): auto-routing only
            // selects ResourceAPI when the tenant's install covers planning-application, and
            // silently falls back to Payload for any un-provisioned tenant (and always does locally,
            // where EAI_LOCAL_PASSIVE synthesizes an install with an empty object-type map). The
            // explicit selector guarantees the create — and the whole downstream chain that echoes
            // its resourceapi ids (migrate/feedback/handoff) — lands in ResourceAPI. Revisit if/when
            // every eai-prod-website tenant install is provisioned so auto-routing can be trusted.
            body.storage_target = 'resourceapi';
            const response = options.signal
                ? await apiService.post(endpoint, body, { signal: options.signal })
                : await apiService.post(endpoint, body);

            logger.debug('[AnonBRService] Anon temp BR created:', response);

            // Capture the B0 ResourceAPI selectors in the return shape (not only at
            // the call site) so any current or future caller receives them. Extraction
            // is total: resourceApi is non-null iff the response carries a resourceapi
            // object. storage_target is a marker only (id presence is authoritative).
            return {
                businessRequest: response?.business_request || null,
                resourceApi: response?.resourceapi
                    ? {
                          planningApplicationId: response.resourceapi.planning_application_id || null,
                          assistConversationId: response.resourceapi.assist_conversation_id || null
                      }
                    : null,
                storageTarget: response?.storage_target || null
            };
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            logger.error('[AnonBRService] Failed to create anon temp BR:', error.message);
            return null;
        }
    }

    /**
     * Request a short-lived DAISY handoff token for the created temp BR.
     * The token is later POSTed to DAISY so it can mint its own signed
     * anonymous-session cookie without exposing the raw session ID in the URL.
     *
     * @param {string} businessRequestId - Temp business request ID
     * @returns {Promise<string|null>} - Handoff token or null on failure
     */
    async createHandoffToken(businessRequestId, options = {}) {
        if (!businessRequestId) {
            logger.warn('[AnonBRService] Cannot create handoff token without businessRequestId');
            return null;
        }

        const endpoint = `/api/proxy/v4/workflows/business-requests/${encodeURIComponent(businessRequestId)}/handoff`;

        // Echo the create's storage decision (same single-send rule as migrate/feedback):
        // when the BR was created in ResourceAPI, tell the handoff validation read to target
        // ResourceAPI so it doesn't fall back to Payload and 404 on a BR that only exists in
        // ResourceAPI. Absent -> empty body, byte-identical to the legacy path. The BR id is
        // in the URL, so only the storage selector is needed (planning-application id == BR id).
        const body = {};
        if (chatStore.resourceApiPlanningApplicationId) {
            body.storage_target = 'resourceapi';
        }

        try {
            logger.debug('[AnonBRService] Creating handoff token:', { businessRequestId });
            const response = options.signal
                ? await apiService.post(endpoint, body, { signal: options.signal })
                : await apiService.post(endpoint, body);
            return response?.handoff_token || null;
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            logger.error('[AnonBRService] Failed to create handoff token:', error.message);
            return null;
        }
    }

    /**
     * Backfill the local anonymous widget transcript into structured storage.
     * This ensures BR claim can transfer the anonymous thread via
     * chat-sessions/chat-messages rather than relying on customData only.
     *
     * @param {string} businessRequestId - Temp business request ID
     * @returns {Promise<Object|null>} - Migration response, skip marker, or null on failure
     */
    async migrateChatHistory(businessRequestId, options = {}) {
        if (!businessRequestId) {
            logger.warn('[AnonBRService] Cannot migrate chat history without businessRequestId');
            return null;
        }

        const payload = buildStructuredHistoryMigrationPayload(options);
        if (!payload?.messages?.length) {
            return {
                skipped: true,
                reason: 'no_messages',
                localMessageIds: []
            };
        }

        const endpoint = `/api/proxy/v4/workflows/business-requests/${encodeURIComponent(businessRequestId)}/migrate-chat-history`;

        try {
            logger.debug('[AnonBRService] Migrating widget chat history:', {
                businessRequestId,
                messageCount: payload.messages.length
            });
            const body = {
                conversation_id: payload.conversation_id,
                stage: payload.stage,
                messages: payload.messages
            };

            // Single send rule (frontend DD §6.3(iv)): inject the explicit ResourceAPI
            // selectors iff a planning-application id is present. Absent -> zero selector
            // keys, byte-identical to the legacy body (FE-AC-020).
            const resourceApiPlanningApplicationId = chatStore.resourceApiPlanningApplicationId;
            if (resourceApiPlanningApplicationId) {
                body.storage_target = 'resourceapi';
                body.planning_application_id = resourceApiPlanningApplicationId;
                const resourceApiAssistConversationId = chatStore.resourceApiAssistConversationId;
                if (resourceApiAssistConversationId) {
                    body.assist_conversation_id = resourceApiAssistConversationId;
                }
            }
            const response = options.signal
                ? await apiService.post(endpoint, body, { signal: options.signal })
                : await apiService.post(endpoint, body);

            const session = response?.session || null;
            // The v4/ResourceAPI migrate endpoint returns the persisted ResourceAPI message
            // ids in submit order (response.messages[i] <-> payload.localMessageIds[i], both
            // built from the same ordered message list). Map them back so downstream
            // per-message ResourceAPI ops (feedback) reference the real persisted id instead
            // of the local UUID. Absent (legacy/Payload path) -> empty map, prior behavior.
            const returnedMessages = Array.isArray(response?.messages) ? response.messages : [];
            const persistedMessageIdMap = {};
            payload.localMessageIds.forEach((localId, i) => {
                const persistedId = returnedMessages[i]?.id;
                if (localId && persistedId) {
                    persistedMessageIdMap[localId] = persistedId;
                }
            });
            chatStore.markMessagesMigrated({
                messageIds: payload.localMessageIds,
                persistedMessageIdMap,
                businessRequestId,
                chatSessionId: session?.chat_session_id || chatStore.chatSessionId,
                ownerType: session?.owner_type || chatStore.ownerType || 'anonymous'
            });

            return {
                ...response,
                localMessageIds: payload.localMessageIds
            };
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            logger.error('[AnonBRService] Failed to migrate widget chat history:', error.message);
            return null;
        }
    }

    /**
     * Build custom data for the business request.
     * Chat messages are intentionally excluded — BR-backed history is stored
     * in structured collections, not customData.chat.messages.
     *
     * @returns {Object} - Custom data object
     */
    buildCustomData() {
        const userState = userStore.getState();

        // If address data exists, the address was submitted → stage is 'planning'
        const hasAddressData = !!(userState.address && userState.propId);

        return {
            // Property state
            property: {
                selectedAddress: userState.address,
                propertyId: userState.propId,
                addressData: {
                    address: userState.address,
                    propId: userState.propId,
                    GURASId: userState.gurasId
                },
                councilName: userState.councilName,
                councilWebsiteURL: userState.councilWebsiteUrl,
                zone: userState.zone,
                zoneDescription: userState.zoneDescription,
                lotSize: userState.lotSize,
                isBushfireZone: userState.bushFireZone,
                isFloodZone: userState.floodZone,
                isHeritageZone: userState.heritageZone,
                lotWidth: userState.lotWidth,
                maxBuildingHeight: userState.maxBuildingHeight,
                minLotSize: userState.minLotSize,
                activePrecinct: userState.activePrecinct,
                propertyPrecinct: userState.propertyPrecinct
            },
            // Stage info
            stage: {
                current: hasAddressData ? 'planning' : 'property'
            },
            user: {
                currentProject: {
                    development_type: userState.currentProject?.development_type || ''
                }
            }
        };
    }
}

// Export singleton instance
export const anonBusinessRequestService = new AnonBusinessRequestService();
